import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { AuthNotice, AuthShell } from '@/components/layout/AuthShell';
import { authService } from '@/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiError';

/** 서버 `@IsEmail()` 이 거절할 값을 미리 걸러 400 을 줄인다. 서버보다 느슨하게 본다 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 백엔드 UpdateUserProfileDto 의 연락처 패턴과 같은 규칙 */
const PHONE_PATTERN = /^[0-9+\-() ]{7,20}$/;
/**
 * 가입 비밀번호 최소 길이. 서버가 가입에만 8자를 요구한다
 * (로그인은 시드된 관리자 계정 때문에 4자로 남아 있다).
 */
const MIN_PASSWORD_LENGTH = 8;

const APPROVAL_NOTICE =
  '관리자 승인 후 이용할 수 있습니다. 승인이 완료되면 등록하신 아이디로 로그인해 주세요.';

interface SignupForm {
  username: string;
  password: string;
  passwordConfirm: string;
  email: string;
  displayName: string;
  contactPhone: string;
  requestedInstitutionName: string;
}

const EMPTY_FORM: SignupForm = {
  username: '',
  password: '',
  passwordConfirm: '',
  email: '',
  displayName: '',
  contactPhone: '',
  requestedInstitutionName: '',
};

type FieldErrors = Partial<Record<keyof SignupForm, string>>;

const validate = (form: SignupForm): FieldErrors => {
  const errors: FieldErrors = {};

  const username = form.username.trim();
  if (!username) errors.username = '아이디를 입력하세요.';
  else if (username.length < 3) errors.username = '아이디는 3자 이상이어야 합니다.';
  else if (/\s/.test(username)) errors.username = '아이디에는 공백을 쓸 수 없습니다.';

  if (form.password.length < MIN_PASSWORD_LENGTH) {
    errors.password = `비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상이어야 합니다.`;
  }
  if (form.passwordConfirm !== form.password) {
    errors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
  }

  const email = form.email.trim();
  if (!email) errors.email = '이메일을 입력하세요.';
  else if (!EMAIL_PATTERN.test(email)) errors.email = '이메일 형식이 올바르지 않습니다.';

  if (!form.displayName.trim()) errors.displayName = '담당자 이름을 입력하세요.';

  const phone = form.contactPhone.trim();
  if (phone && !PHONE_PATTERN.test(phone)) {
    errors.contactPhone = '연락처 형식이 올바르지 않습니다. (예: 010-1234-5678)';
  }

  return errors;
};

/**
 * 기관 담당자 회원가입.
 *
 * 가입만으로는 로그인되지 않는다 — 서버가 토큰을 주지 않고 `status=pending` 으로
 * 계정을 만든다(계약 §2). 그래서 성공 후 곧바로 로그인 화면으로 되돌리며,
 * 승인이 필요하다는 사실을 그 화면에서 다시 알린다.
 */
export const Signup = () => {
  const [form, setForm] = useState<SignupForm>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const setField = (key: keyof SignupForm) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const errors = validate(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setLoading(true);
    try {
      const result = await authService.signup({
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        displayName: form.displayName.trim(),
        // 빈 문자열을 보내면 서버 검증에 걸린다. 값이 있을 때만 싣는다
        contactPhone: form.contactPhone.trim() || undefined,
        requestedInstitutionName: form.requestedInstitutionName.trim() || undefined,
      });

      navigate('/login', {
        replace: true,
        state: { notice: result?.message || APPROVAL_NOTICE },
      });
    } catch (error) {
      setFormError(getApiErrorMessage(error, '가입 신청에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      wide
      title='기관 담당자 회원가입'
      description='기관 담당자 전용 가입 절차입니다. 가입 신청 후 관리자 승인이 완료되어야 로그인할 수 있습니다.'
      footer={
        <Link
          to='/login'
          className='inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700 hover:underline'
        >
          <ArrowLeft className='w-4 h-4' />
          로그인으로 돌아가기
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className='space-y-4'>
        <AuthNotice tone='info' title='승인 후 이용할 수 있습니다'>
          가입만으로는 로그인되지 않습니다. 관리자가 소속 기관을 배정하고 승인해야 기관 담당자
          화면을 이용할 수 있습니다.
        </AuthNotice>

        {formError && (
          <AuthNotice tone='error' title='가입 신청 실패'>
            {formError}
          </AuthNotice>
        )}

        <Input
          label='아이디'
          type='text'
          value={form.username}
          onChange={(e) => setField('username')(e.target.value)}
          error={fieldErrors.username}
          placeholder='영문·숫자 조합 3자 이상'
          autoComplete='username'
        />

        <div className='grid gap-4 sm:grid-cols-2'>
          <Input
            label='비밀번호'
            type='password'
            value={form.password}
            onChange={(e) => setField('password')(e.target.value)}
            error={fieldErrors.password}
            placeholder={`${MIN_PASSWORD_LENGTH}자 이상`}
            autoComplete='new-password'
          />
          <Input
            label='비밀번호 확인'
            type='password'
            value={form.passwordConfirm}
            onChange={(e) => setField('passwordConfirm')(e.target.value)}
            error={fieldErrors.passwordConfirm}
            placeholder='••••••••'
            autoComplete='new-password'
          />
        </div>

        <Input
          label='이메일'
          type='email'
          value={form.email}
          onChange={(e) => setField('email')(e.target.value)}
          error={fieldErrors.email}
          placeholder='manager@example.com'
          autoComplete='email'
        />

        <div className='grid gap-4 sm:grid-cols-2'>
          <Input
            label='담당자 이름'
            type='text'
            value={form.displayName}
            onChange={(e) => setField('displayName')(e.target.value)}
            error={fieldErrors.displayName}
            placeholder='홍길동'
            autoComplete='name'
          />
          <Input
            label='연락처 (선택)'
            type='tel'
            value={form.contactPhone}
            onChange={(e) => setField('contactPhone')(e.target.value)}
            error={fieldErrors.contactPhone}
            placeholder='010-1234-5678'
            autoComplete='tel'
          />
        </div>

        <div>
          <Input
            label='소속 기관명 (선택)'
            type='text'
            value={form.requestedInstitutionName}
            onChange={(e) => setField('requestedInstitutionName')(e.target.value)}
            placeholder='○○요양원'
          />
          <p className='mt-1 text-xs text-gray-500'>
            관리자가 기관을 배정할 때 참고합니다. 실제 배정은 승인 단계에서 관리자가 정합니다.
          </p>
        </div>

        <Button type='submit' className='w-full' loading={loading}>
          가입 신청
        </Button>
      </form>
    </AuthShell>
  );
};
